/**
 * O cadastro do caso pelo painel do dado (E203.F2, ADR-0146 §4): os campos que o exame imprime
 * (nome do cliente, CPF, procedimento, proposta e endereço), com o valor gravado; salvar grava só o
 * que mudou, e o servidor reconfere o caso e refaz a prévia.
 *
 * E203.F4.C (F-E203.F4-07): com fundamento obrigatório, pela rota do painel
 * (`PUT /casos/{id}/dados/cadastro`), que grava o antes e o depois no histórico do caso.
 */
import { useState } from 'react';

import { Field, Input } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { CaseRecordData, CaseUpdateData } from '@/lib/cases';
import { salvarCadastroPeloPainel } from '@/lib/revisaoDoDado';

import { Fundamento, fundamentoValido } from './Fundamento';
import { BotaoSalvar, useGravacao } from './Gravacao';
import type { PropsDoConteudo } from './tipos';

const t = ptBR.painelDoDado.cadastro;

const CAMPOS = ['parties', 'cpf', 'process_number', 'contrato', 'endereco'] as const;
type Campo = (typeof CAMPOS)[number];
type Valores = Record<Campo, string>;

function doCaso(caso: CaseRecordData | null): Valores {
  return Object.fromEntries(CAMPOS.map((c) => [c, caso?.[c] ?? ''])) as Valores;
}

/** Só o que mudou; o campo esvaziado vai como `null` (limpa a coluna). */
function mudancasDe(valores: Valores, inicial: Valores): CaseUpdateData {
  const mudancas: CaseUpdateData = {};
  for (const campo of CAMPOS) {
    const novo = valores[campo].trim();
    if (novo !== inicial[campo].trim()) mudancas[campo] = novo === '' ? null : novo;
  }
  return mudancas;
}

export function CadastroNoPainel({ client, caseId, mesa, acoes, onMudou }: PropsDoConteudo) {
  const inicial = doCaso(mesa.caso);
  const [valores, setValores] = useState<Valores>(inicial);
  const [fundamento, setFundamento] = useState('');
  const mudancas = mudancasDe(valores, inicial);
  const nenhuma = Object.keys(mudancas).length === 0;
  const pode = !nenhuma && fundamentoValido(fundamento);
  const gravacao = useGravacao(
    async () => {
      await salvarCadastroPeloPainel(client, caseId, mudancas, fundamento.trim());
      return null;
    },
    () => {
      acoes.salvo();
      onMudou();
    },
  );
  return (
    <form
      className="space-y-sc-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (pode) void gravacao.salvar();
      }}
      data-testid="painel-cadastro"
    >
      <p className="text-body-sm text-fg-muted">{t.texto}</p>
      {CAMPOS.map((campo) => (
        <Field key={campo} label={t[campo]} htmlFor={`painel-cadastro-${campo}`}>
          <Input
            id={`painel-cadastro-${campo}`}
            value={valores[campo]}
            onChange={(e) => setValores((v) => ({ ...v, [campo]: e.target.value }))}
            data-testid={`painel-cadastro-${campo}`}
          />
        </Field>
      ))}
      {nenhuma && gravacao.estado === 'ocioso' ? (
        <p className="text-caption text-fg-muted">{t.semMudanca}</p>
      ) : null}
      <Fundamento id="painel-cadastro-fundamento" valor={fundamento} onChange={setFundamento} />
      <BotaoSalvar
        estado={gravacao.estado}
        desligado={!pode}
        onSalvar={() => void gravacao.salvar()}
        testId="painel-cadastro-salvar"
      />
    </form>
  );
}
